import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { eventCategoryService as sharedEventCategoryService } from "../../../shared/api/eventCategoryService";
import { PermissionName } from "../../../shared/types/permission";
import { EventCategoryCreateDrawer, EventCategoryUpdateDrawer } from "../eventCategories/EventCategoryDrawer";
import { EventCategoryListContextMenu } from "../eventCategories/EventCategoryListContextMenu";
import { eventCategoryService } from "../shared/api/eventCategoryService";
import { EventCategoryIndexResource } from "../shared/types/eventCategory";

export const EventCategoryList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		eventCategoryService,
		{
			entityName: t("Event category"),
			messages: {
				create: t("Event category was saved."),
				update: t("Event category was saved."),
				delete: t("Event category was deleted."),
			},
		},
		[],
		[sharedEventCategoryService.quickFilterKey],
	);

	const dataListColumns: IDrawerDataListColumn<EventCategoryIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<EventCategoryListContextMenu
						eventCategoryId={id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);
	const canCreate = useHasPermission<PermissionName>(["eventCategory.create"]);
	const canUpdate = useHasPermission<PermissionName>(["eventCategory.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{
				search: "",
				searchFields: [{ name: "name", label: t("Name") }],
			}}
			translations={{
				title: t("Event categories"),
				empty: t("No event categories available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<EventCategoryCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<EventCategoryUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(eventCategoryService.listKey, eventCategoryService.index)}
		/>
	);
};
